import { Box, SvgIcon, Typography, useMediaQuery } from '@mui/material';
import { Star as StarIcon } from '@opengov/react-capital-assets';
import React from 'react';

import { IllustratedMessage } from '../Components/IllustratedMessage';
import { NAV_BAR_MOBILE_BREAKPOINT, NAV_COLOR_PALETTE } from '../Components/styleConstants';
import { useMenuContext } from '../ContextState/useMenuContext';
import { DATA_TEST } from '../enums';

import { FavoritesSection } from './FavoritesSection';
import { initializeFavoritesRefs } from './utils';

const FavoritesInstructions = ({ variant }) => (
  <Typography
    variant={variant}
    component="span"
    data-test={DATA_TEST.favorites_text_instructions}
    color="text.secondary"
  >
    Drag items in the list to reorder.
  </Typography>
);
interface IFavoritesDisplayGridProps {
  emptyFavoritesHeader?: string;
  emptyFavoritesMessage?: string;
  fullPageWidth?: boolean;
}

export const FavoritesDisplayGrid: React.FC<IFavoritesDisplayGridProps> = ({
  emptyFavoritesHeader,
  emptyFavoritesMessage,
  fullPageWidth = false
}) => {
  const { favorites, setFavoritesRefs } = useMenuContext();
  const mobileWidth = useMediaQuery(`(max-width:${NAV_BAR_MOBILE_BREAKPOINT}px)`);
  const header = emptyFavoritesHeader || 'Start adding your favorite workflows.';
  const message =
    emptyFavoritesMessage || 'Find your workflows in the top menu and click on the star icon to keep them here.';

  React.useEffect(() => {
    setFavoritesRefs(initializeFavoritesRefs(favorites));
  }, [favorites]);
  return (
    <Box
      data-test={DATA_TEST.favorites_display_grid}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'background.paper',
        width: '100%'
      }}
    >
      <Box sx={{ display: 'flex' }}>
        <Box sx={{ mr: 1 }}>
          <SvgIcon
            data-test={DATA_TEST.favorites_favorited_icon}
            sx={{ color: NAV_COLOR_PALETTE.favoriteStar }}
            component={StarIcon}
            inheritViewBox
          />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="h4" color="text.primary">
            My Favorites
          </Typography>
          {favorites?.length > 1 && !mobileWidth && <FavoritesInstructions variant="body2" />}
        </Box>
      </Box>
      {favorites?.length > 1 && mobileWidth && <FavoritesInstructions variant="caption" />}
      {favorites?.length ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fit, minmax(250px, ${
              favorites.length < 3 ? (mobileWidth ? '1fr' : '400px') : '1fr'
            }))`,
            gap: 1,
            maxWidth: '100%'
          }}
        >
          {favorites.map((favoriteSection, sectionIndex) => {
            return (
              <FavoritesSection
                key={favoriteSection.id}
                sectionLabel={favoriteSection.label}
                sectionId={favoriteSection.id}
                sectionItems={favoriteSection.items}
                sectionIndex={sectionIndex}
                fullPageWidth={fullPageWidth}
              />
            );
          })}
        </Box>
      ) : (
        <IllustratedMessage
          illustrationType="empty-panel"
          dataTest={DATA_TEST.empty_favorites_view}
          headerText={header}
          messageText={message}
          illustrationHeight={80}
          illustrationWidth={80}
          mobileWidth={mobileWidth}
        />
      )}
    </Box>
  );
};
